import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { SettingsProvider } from "@/contexts/SettingsContext";
import { Toaster } from "@/components/ui/sonner";
import { 
  LoginPage, 
  DashboardPage, 
  UsersPage, 
  ConfigPage, 
  SettingsPage,
  AdminDashboard,
  TemplateManagement,
  CreateTemplatePage,
  ViewTemplatePage,
  EditTemplatePage,
  UserManagement,
  PromptManagement,
  UserDashboard,
  ReviewerDashboard,
  DocumentList,
  CreateDocument,
  DocumentEditor,
} from "@/pages";
import { ROUTES } from "@/constants/routes";
import { 
  ProtectedRoute, 
  PublicRoute, 
  RoleBasedRedirect,
  NotFoundPage,
  UnauthorizedPage,
} from "@/components/auth";
import { USER_ROLES } from "@/constants/beStudyMockData";
import TipTapTestPage from "@/pages/test/TipTapTestPage";

function App() {
  return (
    <SettingsProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Root - Redirect based on auth status and role */}
            <Route path="/" element={<RoleBasedRedirect />} />
            
            {/* Public Routes */}
            <Route 
              path={ROUTES.LOGIN} 
              element={
                <PublicRoute>
                  <LoginPage />
                </PublicRoute>
              } 
            />
            
            {/* Admin Routes */}
            <Route 
              path={ROUTES.ADMIN_DASHBOARD} 
              element={
                <ProtectedRoute allowedRoles={[USER_ROLES.ADMIN]}>
                  <AdminDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path={ROUTES.TEMPLATES} 
              element={
                <ProtectedRoute allowedRoles={[USER_ROLES.ADMIN]}>
                  <TemplateManagement />
                </ProtectedRoute>
              } 
            />
            <Route 
              path={ROUTES.TEMPLATE_CREATE} 
              element={
                <ProtectedRoute allowedRoles={[USER_ROLES.ADMIN]}>
                  <CreateTemplatePage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path={ROUTES.TEMPLATE_VIEW} 
              element={
                <ProtectedRoute allowedRoles={[USER_ROLES.ADMIN]}>
                  <ViewTemplatePage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path={ROUTES.TEMPLATE_EDIT} 
              element={
                <ProtectedRoute allowedRoles={[USER_ROLES.ADMIN]}>
                  <EditTemplatePage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path={ROUTES.USER_MANAGEMENT} 
              element={
                <ProtectedRoute allowedRoles={[USER_ROLES.ADMIN]}>
                  <UserManagement />
                </ProtectedRoute>
              } 
            />
            <Route 
              path={ROUTES.PROMPTS} 
              element={
                <ProtectedRoute allowedRoles={[USER_ROLES.ADMIN]}>
                  <PromptManagement />
                </ProtectedRoute>
              } 
            />
            <Route 
              path={ROUTES.CONFIG} 
              element={
                <ProtectedRoute allowedRoles={[USER_ROLES.ADMIN]}>
                  <ConfigPage />
                </ProtectedRoute>
              } 
            />
            
            {/* User Routes */}
            <Route 
              path={ROUTES.USER_DASHBOARD} 
              element={
                <ProtectedRoute allowedRoles={[USER_ROLES.ADMIN, USER_ROLES.USER]}>
                  <UserDashboard />
                </ProtectedRoute>
              } 
            />
            
            {/* Reviewer Routes */}
            <Route 
              path={ROUTES.REVIEWER_DASHBOARD} 
              element={
                <ProtectedRoute allowedRoles={[USER_ROLES.ADMIN, USER_ROLES.REVIEWER]}>
                  <ReviewerDashboard />
                </ProtectedRoute>
              } 
            />
            
            {/* Document Routes - Accessible by multiple roles */}
            <Route 
              path={ROUTES.DOCUMENTS} 
              element={
                <ProtectedRoute allowedRoles={[USER_ROLES.ADMIN, USER_ROLES.USER, USER_ROLES.REVIEWER]}>
                  <DocumentList />
                </ProtectedRoute>
              } 
            />
            <Route 
              path={ROUTES.DOCUMENT_CREATE} 
              element={
                <ProtectedRoute allowedRoles={[USER_ROLES.ADMIN, USER_ROLES.USER]}>
                  <CreateDocument />
                </ProtectedRoute>
              } 
            />
            <Route 
              path={ROUTES.DOCUMENT_EDITOR} 
              element={
                <ProtectedRoute allowedRoles={[USER_ROLES.ADMIN, USER_ROLES.USER, USER_ROLES.REVIEWER]}>
                  <DocumentEditor />
                </ProtectedRoute>
              } 
            />
            
            {/* Common Routes */}
            <Route 
              path={ROUTES.SETTINGS} 
              element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              } 
            />
            
            {/* Legacy Routes - Redirect to new paths */}
            <Route 
              path={ROUTES.DASHBOARD} 
              element={<RoleBasedRedirect />} 
            />
            <Route 
              path={ROUTES.USERS} 
              element={<Navigate to={ROUTES.USER_MANAGEMENT} replace />} 
            />
            
            {/* Error Pages */}
            <Route path="/unauthorized" element={<UnauthorizedPage />} />
            <Route path="/404" element={<NotFoundPage />} />

            {/* Isolated TipTap Test Page — TEMPORARY */}
            <Route path="/test/tiptap-editor" element={<TipTapTestPage />} />
            
            {/* Catch all - 404 */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          
          {/* Toast Notifications */}
          <Toaster />
        </BrowserRouter>
      </AuthProvider>
    </SettingsProvider>
  );
}

export default App;
